import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, Container, Form, Modal, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URI } from "../Common/data";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import Loader from "../loader/Loader";
import { formatTimeRange } from "../Utils/timeUtils";

function DirectBooking() {
  const { theaterId, timeSlotId, slotId } = useParams();
  const isMobileView = typeof window !== "undefined" && window.innerWidth < 700;

  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("cine_userToken")
  );
  const [theaterData, setTheaterData] = useState(null);
  const [slotData, setSlotData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [allData, setAllData] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [coupons, setCoupons] = useState([]);
  const [couponLoading, setCouponLoading] = useState(false);
  const [showDecorationBurst, setShowDecorationBurst] = useState(false);
  const decorationBurstTimerRef = useRef(null);
  const [selectedTimeSlotData, setSelectedTimeSlotData] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [showBookingNameHint, setShowBookingNameHint] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({
    bookingName: "",
    eventType: "",
    numberOfPeople: 1,
    specialRequests: "",
    decoration: true,
    couponCode: "",
    theaterId,
    totalPrice: 0,
    advanceAmount: 0,
    balanceAmount: 0,
    additionalCostPerPerson: 50,
    decorCost: 200
  });
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    mobile: "",
    password: "",
    otp: ""
  });
  const [otpSent, setOtpSent] = useState(false);
  const [showSendOtpButton, setShowSendOtpButton] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    setShowSendOtpButton(formData.mobile.trim().length >= 10);
  }, [formData.mobile]);

  useEffect(() => {
    const savedUser = localStorage.getItem("cine_user");
    if (!savedUser) return;

    try {
      const parsedUser = JSON.parse(savedUser);
      if (parsedUser?.mobile) {
        setFormData((prev) => ({
          ...prev,
          mobile: prev.mobile || parsedUser.mobile,
          password: prev.password || parsedUser.mobile
        }));
      }
    } catch (error) {
      // Ignore malformed stored user data.
    }
  }, []);

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        setCouponLoading(true);
        const res = await axios.get(`${API_URI}/coupons/valid/for-user`);
        if (res.status === 200 && Array.isArray(res.data?.data)) {
          setCoupons(res.data.data);
        } else {
          setCoupons([]);
        }
      } catch (error) {
        setCoupons([]);
      } finally {
        setCouponLoading(false);
      }
    };

    fetchCoupons();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setPageLoading(true);
        const theaterRes = await axios.get(`${API_URI}/theater/${theaterId}`);
        if (theaterRes.status === 200) {
          setTheaterData(theaterRes?.data?.data);
        }

        const slotRes = await axios.get(`${API_URI}/slot/${timeSlotId}`);
        if (slotRes.status === 200) {
          const slotInfo = slotRes?.data?.data;
          setAllData(slotInfo);
          const selectedSlot = slotInfo.slots.find((slot) => slot._id === slotId);

          if (selectedSlot) {
            const selectedTimeSlotId = selectedSlot.timeSlotId;
            const findSlot = slotInfo?.theater?.timeSlots.find(
              (item) => item?._id === selectedTimeSlotId
            );

            selectedSlot.price = findSlot.price;
            setSlotData(selectedSlot);
            setBookingDetails((prev) => ({
              ...prev,
              numberOfPeople: findSlot.defaultPeople,
              totalPrice:
                Number(selectedSlot.price) +
                  Number(theaterRes?.data?.data?.decorCost) || 0,
              additionalCostPerPerson: findSlot.additionalCostPerPerson,
              decorCost: theaterRes?.data?.data?.decorCost || 0,
              decoration: true
            }));

            const timeSlotData = theaterRes?.data?.data?.timeSlots.find(
              (item) => item?._id === selectedTimeSlotId
            );
            setSelectedTimeSlotData(timeSlotData);
          } else {
            toast.error("Selected slot not found!");
          }
        }
      } catch (error) {
        toast.error("Failed to load booking details!");
      } finally {
        setPageLoading(false);
      }
    };

    fetchData();
  }, [theaterId, timeSlotId, slotId]);

  const applyCoupon = async (couponCode = coupon) => {
    try {
      const token = localStorage.getItem("cine_userToken");
      if (!token) return toast.error("Please Login First.");
      if (!couponCode) return toast.error("Please choose or enter a coupon code.");

      setLoading(true);
      const response = await axios.post(
        `${API_URI}/coupons/validate`,
        {
          code: couponCode,
          cartValue: bookingDetails.totalPrice,
          theaterPrice: selectedTimeSlotData.price
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.status === 200) {
        const discount = response?.data?.data?.discountAmount;
        setDiscountAmount(discount);
        toast.success(response?.data?.data?.message);
        setCoupon(couponCode);
        setBookingDetails((prev) => ({ ...prev, couponCode: couponCode }));
        calculateTotalPrice(discount);
      } else {
        toast.error("Invalid coupon code");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
    setLoading(false);
  };

  const getCouponDescription = (activeCoupon) => {
    let description = "";
    if (activeCoupon.discountType === "percentage") {
      description = `${activeCoupon.discountValue}% off`;
    } else {
      description = `Save Rs. ${activeCoupon.discountValue}`;
    }

    if (activeCoupon.minPurchaseAmount > 0) {
      description += ` above Rs. ${activeCoupon.minPurchaseAmount}`;
    }

    if (activeCoupon.maxDiscount) {
      description += ` · max Rs. ${activeCoupon.maxDiscount}`;
    }

    return description;
  };

  const handleCouponSelect = async (couponCode) => {
    if (!couponCode) return;
    setCoupon(couponCode);
    if (bookingDetails.couponCode === couponCode && discountAmount > 0) {
      return;
    }
    await applyCoupon(couponCode);
  };

  const triggerDecorationBurst = () => {
    setShowDecorationBurst(false);
    window.clearTimeout(decorationBurstTimerRef.current);
    decorationBurstTimerRef.current = window.setTimeout(() => {
      setShowDecorationBurst(true);
      decorationBurstTimerRef.current = window.setTimeout(() => {
        setShowDecorationBurst(false);
      }, 4800);
    }, 20);
  };

  const handleDecorationToggle = () => {
    const nextValue = !bookingDetails.decoration;
    setBookingDetails({
      ...bookingDetails,
      decoration: nextValue
    });
    if (nextValue) {
      triggerDecorationBurst();
    }
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(decorationBurstTimerRef.current);
    };
  }, []);

  const calculateTotalPrice = (discount) => {
    if (!selectedTimeSlotData) return;

    let total = selectedTimeSlotData.price || 0;
    const guestCount = Number(bookingDetails.numberOfPeople) || 0;
    const extraPeople = Math.max(guestCount - selectedTimeSlotData.defaultPeople, 0);
    const extraCost = extraPeople * selectedTimeSlotData.additionalCostPerPerson;

    total += extraCost;

    if (bookingDetails.decoration) {
      total += bookingDetails.decorCost;
    }

    total -= Number(discount || 0);

    setBookingDetails((prev) => ({
      ...prev,
      totalPrice: total
    }));
  };

  useEffect(() => {
    if (!selectedTimeSlotData) return;

    let total = selectedTimeSlotData.price || 0;
    const guestCount = Number(bookingDetails.numberOfPeople) || 0;
    const extraPeople = Math.max(guestCount - selectedTimeSlotData.defaultPeople, 0);
    const extraCost = extraPeople * selectedTimeSlotData.additionalCostPerPerson;

    total += extraCost;

    if (bookingDetails.decoration) {
      total += bookingDetails.decorCost;
    }

    total -= Number(discountAmount || 0);

    setBookingDetails((prev) => ({
      ...prev,
      totalPrice: total
    }));
  }, [
    bookingDetails.numberOfPeople,
    bookingDetails.decoration,
    bookingDetails.decorCost,
    discountAmount,
    selectedTimeSlotData
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { mobile } = formData;
    const { bookingName } = bookingDetails;
    const guestCount = Number(bookingDetails.numberOfPeople) || 0;

    if (guestCount < 1) {
      toast.error("Number of people must be at least 1!");
      return;
    }
    if (guestCount > theaterData?.capacity) {
      toast.error(
        `Number of people cannot exceed theater capacity of ${theaterData?.capacity}!`
      );
      return;
    }
    if (bookingName.trim() === "") {
      toast.error("Booking name is required!");
      return;
    }
    if (!mobile) {
      toast.error("Mobile number is required!");
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URI}/user/login-or-signup`, {
        ...formData,
        password: mobile,
        name: bookingDetails.bookingName
      });
      setLoading(false);
      setOtpSent(true);
      setResendTimer(15);
      toast.success("OTP sent to your mobile number!");
    } catch (error) {
      setLoading(false);
      const errorMsg =
        error.response?.data?.message || "Something went wrong, try again!";
      toast.error(errorMsg);
    }
  };

  const handleOtpSubmit = async () => {
    const { mobile, otp } = formData;
    try {
      setLoading(true);
      const response = await axios.post(`${API_URI}/user/create/verify`, {
        mobile,
        otp
      });

      if (response.status === 200) {
        const data = response.data.data;
        localStorage.setItem("cine_user", JSON.stringify(data.user));
        localStorage.setItem("cine_userToken", data.tokens.accessToken);
        setIsLoggedIn(true);
        toast.success("User registered successfully!");
        setFormData((prev) => ({
          ...prev,
          email: "",
          name: "",
          password: prev.mobile,
          otp: ""
        }));
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error(error?.response?.data?.message || "Invalid OTP, try again!");
    }
  };

  const preBookingAmount =
    bookingDetails.totalPrice > 1020
      ? 1020
      : Number(bookingDetails.totalPrice) * 1.02;
  const normalizedMobile = formData.mobile.replace(/\D/g, "");
  const isBookingFormComplete =
    isLoggedIn &&
    bookingDetails.bookingName.trim() &&
    bookingDetails.eventType.trim() &&
    normalizedMobile.length >= 10 &&
    Number(bookingDetails.numberOfPeople) >= 1 &&
    Number(bookingDetails.numberOfPeople) <=
      Number(selectedTimeSlotData?.maxPeople || Infinity);

  const handleBookingSubmit = async () => {
    const token = localStorage.getItem("cine_userToken");
    if (!token) return toast.error("Please Login First.");

    if (allData?.theater?.capacity < Number(bookingDetails.numberOfPeople)) {
      return toast.error(`Theater capacity is ${allData?.theater?.capacity}`);
    }

    const baseSlotPrice = selectedTimeSlotData?.price;
    const defaultPeople = selectedTimeSlotData?.defaultPeople || 2;
    const additionalCostPerPerson =
      selectedTimeSlotData?.additionalCostPerPerson || 0;
    const extraPeopleCount =
      Number(bookingDetails.numberOfPeople) > defaultPeople
        ? Number(bookingDetails.numberOfPeople) - defaultPeople
        : 0;
    const additionalPeopleCost = extraPeopleCount * additionalCostPerPerson;

    const breakdownNew = getPriceBreakdown();
    const advanceAmount = preBookingAmount;
    const balanceAmount =
      Number(bookingDetails.totalPrice) * 1.02 - advanceAmount;
    const payload = {
      ...bookingDetails,
      bookingDate: allData?.date,
      startTime: slotData?.startTime,
      endTime: slotData?.endTime,
      timeSlotId: slotData?.timeSlotId,
      pricingDetails: {
        baseSlotPrice,
        extraPeopleCount,
        additionalCostPerPerson,
        additionalPeopleCost,
        decorCost: bookingDetails.decorCost || 0,
        discountAmount: discountAmount || 0,
        totalPrice: bookingDetails.totalPrice,
        taxBreakdown: {
          baseAmount: breakdownNew.baseAmount,
          cgst: breakdownNew.cgst,
          sgst: breakdownNew.sgst,
          platformFee: breakdownNew.platformFee,
          finalTotal: breakdownNew.finalTotal
        },
        advanceAmount,
        balanceAmount
      },
      advanceAmount,
      balanceAmount,
      subLocationId: theaterData?.subLocationId
    };

    setLoading(true);
    try {
      const response = await axios.post(`${API_URI}/booking/create`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        const bookingData = response?.data?.data;
        const finalAmount = Number(preBookingAmount).toFixed(2).toString();
        const paymentRes = await axios.post(`${API_URI}/phonepe/payment`, {
          name: bookingData?.bookingName,
          amount: finalAmount,
          merchantUserId: bookingData._id
        });
        window.location.href = paymentRes.data;
      } else {
        toast.error("Booking failed. Please try again.");
      }
    } catch (error) {
      toast.error("Booking failed. Please try again.");
    }
    setLoading(false);
  };

  const getPriceBreakdown = () => {
    const total = bookingDetails.totalPrice;
    const base = total / 1.18;
    const cgst = Math.floor(base * 0.09 * 100) / 100;
    const sgst = Math.floor(base * 0.09 * 100) / 100;
    const platformFee = Math.floor(total * 0.02 * 100) / 100;

    return {
      baseAmount: base.toFixed(2),
      cgst: cgst.toFixed(2),
      sgst: sgst.toFixed(2),
      platformFee: platformFee.toFixed(2),
      finalTotal: (total + platformFee).toFixed(2)
    };
  };

  const breakdown = getPriceBreakdown();

  const selectedDateLabel = useMemo(() => {
    if (!allData?.date) return "Date not available";
    return new Date(allData.date).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  }, [allData?.date]);

  const slotLabel = formatTimeRange(
    selectedTimeSlotData?.startTime,
    selectedTimeSlotData?.endTime
  );
  const extraPeopleCount = Math.max(
    (Number(bookingDetails.numberOfPeople) || 0) -
      (selectedTimeSlotData?.defaultPeople || 0),
    0
  );
  const extraPeopleCost =
    extraPeopleCount * (selectedTimeSlotData?.additionalCostPerPerson || 0);
  const theaterImage =
    theaterData?.imageUrls?.[0] || theaterData?.images?.[0] || "";
  const serviceList = Array.isArray(theaterData?.services)
    ? theaterData.services
    : [];

  if (pageLoading) {
    return (
      <>
        <Navbar />
        <Container className="book-now-shell direct-booking-shell">
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "60vh", gap: "14px" }}
          >
            <Spinner animation="border" variant="primary" />
            <p className="booking-hero-copy" style={{ margin: 0 }}>
              Loading your booking details...
            </p>
          </div>
        </Container>
        <Footer />
      </>
    );
  }

  if (!theaterData || !slotData) {
    return <h5 className="text-center mt-5">Booking details not available.</h5>;
  }

  return (
    <>
      {loading && <Loader />}
      <Navbar />
      <Container className="book-now-shell direct-booking-shell">
        <div className="updated-booking direct-booking-layout d-flex">
          <div className="updated-booking-left direct-booking-main">
            <div className="booking-section-heading direct-booking-heading">
              <p className="booking-section-kicker">Final step</p>
              <h3>Complete your slot booking</h3>
            </div>

            <Card className="booking-hero-card direct-booking-hero-card">
              <Card.Body>
                <div className="direct-booking-hero">
                  <div className="direct-booking-hero__media">
                    {theaterImage ? (
                      <img
                        src={theaterImage}
                        alt={theaterData?.name}
                        className="direct-booking-hero__image"
                      />
                    ) : (
                      <div className="direct-booking-hero__image direct-booking-hero__image--empty">
                        Private screening room
                      </div>
                    )}
                  </div>
                  <div className="direct-booking-hero__content">
                    <div className="direct-booking-hero__title">
                      <p className="booking-hero-sidecard__eyebrow">Selected theater</p>
                      <h4>{theaterData?.name}</h4>
                      <p className="direct-booking-hero__subtitle">
                        Lock your preferred slot, confirm guest count, and finish
                        the pre-booking in one place.
                      </p>
                    </div>

                    <div className="booking-hero-stats direct-booking-hero__stats">
                      <div className="booking-hero-stat">
                        <span>Event date</span>
                        <strong>{selectedDateLabel}</strong>
                      </div>
                      <div className="booking-hero-stat">
                        <span>Slot time</span>
                        <strong>{slotLabel}</strong>
                      </div>
                      <div className="booking-hero-stat">
                        <span>Total guests</span>
                        <strong>{bookingDetails.numberOfPeople || 0}</strong>
                      </div>
                      <div className="booking-hero-stat">
                        <span>Decoration</span>
                        <strong>
                          {bookingDetails.decoration ? "Included" : "Not Included"}
                        </strong>
                      </div>
                    </div>

                    {serviceList.length > 0 && (
                      <div className="booking-theater-card__tags direct-booking-tags">
                        {serviceList.slice(0, 6).map((service, index) => (
                          <div className="theater-tag-chip" key={`${service?.name || "service"}-${index}`}>
                            <span className="theater-tag-chip__icon">+</span>
                            <span className="theater-tag-chip__label">
                              {service?.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="booking-hero-card direct-booking-form-card">
              <Card.Body>
                <div className="direct-booking-formcard__surface">
                {showDecorationBurst && (
                  <div className="direct-booking-confetti" aria-hidden="true">
                    {Array.from({ length: 18 }).map((_, index) => (
                      <span key={index} className={`direct-booking-confetti__piece direct-booking-confetti__piece--${(index % 6) + 1}`} />
                    ))}
                  </div>
                )}

                <div className="direct-booking-formcard__header">
                  <div>
                    <p className="booking-hero-sidecard__eyebrow">Booking form</p>
                    <h4>Customer info, verification, and add-ons</h4>
                  </div>
                </div>

                <div className="direct-booking-formgrid direct-booking-formgrid--primary">
                  <Form.Group className="direct-booking-field">
                    <Form.Label>Booking Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={bookingDetails.bookingName}
                      onChange={(e) =>
                        setBookingDetails({
                          ...bookingDetails,
                          bookingName: e.target.value
                        })
                      }
                      onBlur={() =>
                        setShowBookingNameHint(
                          Boolean(bookingDetails.bookingName.trim())
                        )
                      }
                      placeholder="Whose event is this for?"
                    />
                    {showBookingNameHint && bookingDetails.bookingName.trim() && (
                      <p className="direct-booking-field__hint">
                        That name deserves a blockbuster celebration.
                      </p>
                    )}
                  </Form.Group>

                  <Form.Group className="direct-booking-field">
                    <Form.Label>
                      Total Guests (Maximum {selectedTimeSlotData?.maxPeople})
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={bookingDetails.numberOfPeople}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val) || val === "") {
                          const numeric = val;
                          if (
                            numeric <= selectedTimeSlotData?.maxPeople ||
                            numeric === ""
                          ) {
                            setBookingDetails({
                              ...bookingDetails,
                              numberOfPeople: numeric
                            });
                          }
                        }
                      }}
                      placeholder={`Up to ${selectedTimeSlotData?.maxPeople} people`}
                    />
                    <p className="direct-booking-field__hint">
                      {selectedTimeSlotData?.defaultPeople} Guest Included, Extra Guests
                      {" "}Rs.{selectedTimeSlotData?.additionalCostPerPerson || 0}*{""}
                      {extraPeopleCount}={extraPeopleCost}
                    </p>
                  </Form.Group>

                  <Form.Group className="direct-booking-field">
                    <Form.Label>Event Type</Form.Label>
                    <Form.Select
                      value={bookingDetails.eventType}
                      onChange={(e) => {
                        setBookingDetails({
                          ...bookingDetails,
                          eventType: e.target.value
                        });
                      }}
                    >
                      <option value="">Select event type</option>
                      <option value="Birthday">Birthday</option>
                      <option value="Anniversary">Anniversary</option>
                      <option value="Proposal">Proposal</option>
                      <option value="Date Night">Date Night</option>
                      <option value="Bride To Be">Bride To Be</option>
                      <option value="Surprise Celebration">Surprise Celebration</option>
                      <option value="Other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="direct-booking-formgrid direct-booking-formgrid--secondary">
                  <Form.Group className="direct-booking-field">
                    <Form.Label>Contact Number</Form.Label>
                    <div className="direct-booking-contact-field">
                      <Form.Control
                        type="text"
                        className="direct-booking-contact-field__input"
                        value={formData.mobile}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mobile: e.target.value,
                            password: e.target.value
                          })
                        }
                        placeholder="Enter mobile number"
                      />
                      {!isLoggedIn && showSendOtpButton && (
                        <Button
                          className="coupon-1 direct-booking-contact-field__button"
                          onClick={handleSubmit}
                          disabled={resendTimer > 0}
                        >
                          {otpSent
                            ? resendTimer > 0
                              ? `Resend OTP in ${resendTimer}s`
                              : "Resend OTP"
                            : "Generate OTP"}
                        </Button>
                      )}
                    </div>
                    <p className="direct-booking-field__hint">
                      {isLoggedIn
                        ? "This contact number is already verified."
                        : "Generate OTP here to verify before payment."}
                    </p>

                    {!isLoggedIn && otpSent && isMobileView && (
                      <div className="direct-booking-auth direct-booking-auth--compact direct-booking-auth--inline-mobile">
                        <div className="direct-booking-auth__header">
                          <p className="booking-hero-sidecard__eyebrow">Verification</p>
                          <h5>Enter OTP to verify</h5>
                          <p>
                            Complete verification here and then continue to payment.
                          </p>
                        </div>

                        <div className="direct-booking-contact-row">
                          <Form.Control
                            type="text"
                            className="direct-booking-auth__otp-input"
                            value={formData.otp}
                            onChange={(e) =>
                              setFormData({ ...formData, otp: e.target.value })
                            }
                            placeholder="Enter OTP"
                          />
                          <Button className="coupon-2 direct-booking-inlinebutton direct-booking-toggle__button direct-booking-toggle__button--action" onClick={handleOtpSubmit}>
                            Verify OTP
                          </Button>
                        </div>
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="direct-booking-field">
                    <Form.Label>
                      Decoration (Rs. {bookingDetails.decorCost})
                    </Form.Label>
                    <button
                      type="button"
                      className="coupon-1 direct-booking-toggle__button direct-booking-toggle__button--standalone"
                      data-active={bookingDetails.decoration}
                      onClick={handleDecorationToggle}
                      aria-pressed={bookingDetails.decoration}
                      disabled
                    >
                      {bookingDetails.decoration ? "Included" : "Not Included"}
                    </button>
                    <p
                      className="direct-booking-field__hint direct-booking-decoration__hint"
                      data-active={bookingDetails.decoration}
                    >
                      {bookingDetails.decoration
                        ? ""
                        : "Add decoration to make the moment feel complete."}
                    </p>
                  </Form.Group>

                  <Form.Group className="direct-booking-field">
                    <Form.Label>Any Special Requests</Form.Label>
                    <Form.Control
                      type="text"
                      value={bookingDetails.specialRequests}
                      onChange={(e) =>
                        setBookingDetails({
                          ...bookingDetails,
                          specialRequests: e.target.value
                        })
                      }
                      placeholder="Share any requests"
                    />
                  </Form.Group>
                </div>

                {!isLoggedIn && otpSent && !isMobileView && (
                  <div className="direct-booking-auth direct-booking-auth--compact">
                    <div className="direct-booking-auth__header">
                      <p className="booking-hero-sidecard__eyebrow">Verification</p>
                      <h5>Enter OTP to verify</h5>
                      <p>
                        Complete verification here and then continue to payment.
                      </p>
                    </div>

                    <div className="direct-booking-contact-row">
                      <Form.Control
                        type="text"
                        className="direct-booking-auth__otp-input"
                        value={formData.otp}
                        onChange={(e) =>
                          setFormData({ ...formData, otp: e.target.value })
                        }
                        placeholder="Enter OTP"
                      />
                      <Button className="coupon-2 direct-booking-inlinebutton direct-booking-toggle__button direct-booking-toggle__button--action" onClick={handleOtpSubmit}>
                        Verify OTP
                      </Button>
                    </div>
                  </div>
                )}

                {/* Offers and pricing */}
                <div className="direct-booking-offers">
                  <div className="direct-booking-offers__header">
                    <div>
                      <p className="booking-hero-sidecard__eyebrow">Offers</p>
                      <h5>Choose an active coupon</h5>
                    </div>
                    {discountAmount > 0 && (
                      <span className="direct-booking-offers__applied">
                        Applied: {bookingDetails.couponCode}
                      </span>
                    )}
                  </div>

                  <div className="direct-booking-coupon-row">
                    <Form.Control
                      type="text"
                      className="direct-booking-coupon-row__input"
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value.trim().toUpperCase())}
                      placeholder="Enter coupon code manually"
                    />
                    <Button
                      className="coupon-1 direct-booking-inlinebutton direct-booking-toggle__button direct-booking-toggle__button--action"
                      onClick={() => applyCoupon()}
                      disabled={discountAmount > 0 && bookingDetails.couponCode === coupon}
                    >
                      {discountAmount > 0 && bookingDetails.couponCode === coupon ? "Applied" : "Apply"}
                    </Button>
                  </div>

                  {couponLoading ? (
                    <div className="direct-booking-offers__empty">Loading active offers...</div>
                  ) : coupons.length > 0 ? (
                    <div className="direct-booking-offers__grid">
                      {coupons.map((activeCoupon) => {
                        const isApplied = bookingDetails.couponCode === activeCoupon.code && discountAmount > 0;
                        const meetsMinimum =
                          !activeCoupon.minPurchaseAmount ||
                          Number(bookingDetails.totalPrice) >= Number(activeCoupon.minPurchaseAmount);
                        return (
                          <button
                            type="button"
                            key={activeCoupon._id || activeCoupon.code}
                            className="direct-booking-offer-card"
                            data-active={isApplied}
                            data-disabled={!meetsMinimum}
                            onClick={() => meetsMinimum && handleCouponSelect(activeCoupon.code)}
                            disabled={!meetsMinimum}
                          >
                            <span className="direct-booking-offer-card__code">
                              {activeCoupon.code}
                            </span>
                            <strong>{getCouponDescription(activeCoupon)}</strong>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="direct-booking-offers__empty">
                      No active coupons right now. You can still continue with the booking.
                    </div>
                  )}
                </div>

                <div className="coupon-btns direct-booking-actions">
                  <Button
                    className="coupon-2"
                    onClick={() => setShowPaymentModal(true)}
                    disabled={!isBookingFormComplete}
                  >
                    {`Pay Rs. ${Number(preBookingAmount).toFixed(2)} to Book`}
                  </Button>
                </div>
                </div>
              </Card.Body>
            </Card>
          </div>

        </div>
      </Container>

      <Footer />

      <Modal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        centered
        dialogClassName="booking-summary-modal"
        contentClassName="booking-summary-modal__content"
      >
        <Modal.Body className="booking-summary-modal__body">
          <div className="booking-summry booking-summry--modal direct-booking-summary direct-booking-summary--modal">
            <p className="booking-summary-kicker">Payment confirmation</p>
            <h3>Review before payment</h3>

            <div className="selected-details">
              <p>Booking Name</p>
              <h5>{bookingDetails.bookingName || "Add booking name"}</h5>
            </div>

            <div className="selected-details">
              <p>Booking Info</p>
              <h5>
                {theaterData?.name} {selectedDateLabel} {slotLabel}
              </h5>
            </div>

            <div className="selected-details">
              <p>Theater Price</p>
              <h5>Rs. {selectedTimeSlotData?.price}</h5>
            </div>

            {bookingDetails.decoration && (
              <div className="selected-details">
                <p>Decoration</p>
                <h5>Rs. {bookingDetails.decorCost}</h5>
              </div>
            )}

            {extraPeopleCount > 0 && (
              <div className="selected-details">
                <p>Extra Guests</p>
                <h5>Rs. {extraPeopleCost}</h5>
              </div>
            )}

            <div className="selected-details">
              <p>Total Amount</p>
              <h5>
                Rs. {bookingDetails.totalPrice}
              </h5>
            </div>

            {discountAmount > 0 && (
              <div className="selected-details">
                <p>Coupon Discount</p>
                <h5>- Rs. {discountAmount}</h5>
              </div>
            )}

            <div className="selected-details">
              <p>Tax Breakup</p>
              <h5>
                Rs. {breakdown.baseAmount} + Rs.{" "}
                {(
                  Number(breakdown.cgst) + Number(breakdown.sgst)
                ).toFixed(2)}
              </h5>
            </div>

            <div className="selected-details">
              <p>Platform Fee</p>
              <h5>Rs. {breakdown.platformFee}</h5>
            </div>

            <div className="selected-details">
              <p>Total Payable</p>
              <h5>Rs. {breakdown.finalTotal}</h5>
            </div>

            <div className="booking-summary-note">
              Pay Rs.{Number(preBookingAmount)} now to confirm your
              booking. Feel free as we have full refund policy. For more details check our cancellation policy.
            </div>

            <div className="coupon-btns direct-booking-modal__actions">
              <Button className="coupon-2" onClick={handleBookingSubmit}>
                Pay {Number(preBookingAmount).toFixed(2)} to Book
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default DirectBooking;
